import { Button, Dialog, Flex, Text, Spinner } from "@radix-ui/themes";
import { Trans, useTranslation } from "react-i18next";
import { useManagerContext } from "./context";

type DeleteDialogProps<T> = {
  getItemName: (item: T) => string;
  onDelete: (item: T) => Promise<void>;
};

export function DeleteDialog<T>({
  getItemName,
  onDelete,
}: DeleteDialogProps<T>) {
  const { t } = useTranslation();
  const {
    deleteDialogOpen,
    closeDeleteDialog,
    selectedItem,
    deleting,
    setDeleting,
    error,
    setError,
    refresh,
  } = useManagerContext<T>();

  const handleDelete = async () => {
    if (!selectedItem) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(selectedItem);
      closeDeleteDialog();
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog.Root
      open={deleteDialogOpen}
      onOpenChange={(open) => !open && closeDeleteDialog()}
    >
      <Dialog.Content maxWidth="400px">
        <Dialog.Title>{t("delete.confirmTitle")}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          <Trans
            i18nKey="delete.confirmMessage"
            values={{ name: selectedItem ? getItemName(selectedItem) : "" }}
            components={{ bold: <Text weight="bold" /> }}
          />
        </Dialog.Description>

        {error && (
          <Text size="2" color="red" mb="2">
            {error}
          </Text>
        )}

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={deleting}>
              {t("common.cancel")}
            </Button>
          </Dialog.Close>
          <Button color="red" onClick={handleDelete} disabled={deleting}>
            {deleting && <Spinner size="1" />}
            {deleting ? t("common.deleting") : t("common.delete")}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
